import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BookmarkButton from "../components/BookmarkButton";
import useBookmarks from "../hooks/useBookmarks";
import SEO from "../components/SEO";
import { getEpisode, getArticle } from "../mock";
import { Bookmark, Play, ArrowRight } from "lucide-react";

const SavedPage = () => {
  const { items } = useBookmarks();

  const savedEpisodes = items
    .filter((b) => b.type === "episode")
    .map((b) => getEpisode(b.id))
    .filter(Boolean);
  const savedArticles = items
    .filter((b) => b.type === "article")
    .map((b) => getArticle(b.id))
    .filter(Boolean);

  const empty = savedEpisodes.length === 0 && savedArticles.length === 0;
  const nice = (c) => (c ? c.charAt(0) + c.slice(1).toLowerCase() : "");

  return (
    <div className="bg-[#eceae5] min-h-screen">
      <SEO title="Your Reading List" description="Everything you've saved on 20 PROOF, kept safe in your browser." type="website" />
      <Navbar solid />

      <header className="pt-36 md:pt-44 pb-8 px-6 md:px-10 max-w-[1400px] mx-auto">
        <p className="font-sans-ui uppercase tracking-[0.2em] text-sm text-neutral-500">Save For Later</p>
        <h1 className="font-display text-5xl md:text-7xl text-neutral-900 mt-3" style={{ fontWeight: 700 }}>
          Your Reading List
        </h1>
        <p className="font-display italic text-neutral-600 text-lg md:text-xl mt-4">
          Everything you’ve saved, kept safe in your browser.
        </p>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        {empty ? (
          <div className="flex flex-col items-center text-center py-24">
            <div className="w-16 h-16 rounded-full border border-neutral-300 flex items-center justify-center">
              <Bookmark size={26} className="text-neutral-400" />
            </div>
            <p className="font-display text-2xl text-neutral-700 mt-6">Nothing saved yet</p>
            <p className="font-display text-neutral-500 mt-2 max-w-sm">
              Tap the bookmark icon on any episode or article to keep it here for later.
            </p>
            <div className="flex gap-3 mt-8">
              <Link to="/episodes" className="bg-neutral-900 hover:bg-neutral-700 text-white font-sans-ui text-sm px-7 py-3.5 rounded-full transition-colors">
                Browse episodes
              </Link>
              <Link to="/topics/lifestyle" className="border border-neutral-300 hover:border-neutral-900 text-neutral-800 font-sans-ui text-sm px-7 py-3.5 rounded-full transition-colors">
                Read articles
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-16">
            {savedEpisodes.length > 0 && (
              <section>
                <h2 className="font-display text-3xl text-neutral-900 mb-6" style={{ fontWeight: 700 }}>
                  Episodes
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {savedEpisodes.map((ep) => (
                    <div key={ep.id} className="group">
                      <Link to={`/episode/${ep.id}`} className="block">
                        <div className="relative w-full aspect-video overflow-hidden rounded-md">
                          <BookmarkButton type="episode" id={ep.id} />
                          <img src={ep.thumbnail} alt={ep.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-white/85 flex items-center justify-center">
                              <Play size={18} className="text-neutral-900 ml-0.5" fill="currentColor" />
                            </div>
                          </div>
                        </div>
                        <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500 mt-3">
                          {ep.number} · {nice(ep.category)}
                        </p>
                        <h3 className="font-display text-xl text-neutral-900 mt-1" style={{ fontWeight: 700 }}>{ep.title}</h3>
                      </Link>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {savedArticles.length > 0 && (
              <section>
                <h2 className="font-display text-3xl text-neutral-900 mb-6" style={{ fontWeight: 700 }}>
                  Articles
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
                  {savedArticles.map((a) => (
                    <Link key={a.slug} to={`/article/${a.slug}`} className="group">
                      <div className="relative w-full aspect-[4/3] overflow-hidden rounded-md">
                        <BookmarkButton type="article" id={a.slug} />
                        <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105" />
                      </div>
                      <p className="font-sans-ui text-xs uppercase tracking-widest text-neutral-500 mt-3">
                        {nice(a.category)} · {a.readTime}
                      </p>
                      <h3 className="font-display text-xl text-neutral-900 mt-1" style={{ fontWeight: 700 }}>{a.title}</h3>
                      <span className="inline-flex items-center gap-1.5 font-sans-ui text-sm text-neutral-900 mt-2">
                        Read <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SavedPage;
